import { useTranslation } from 'react-i18next';
import { UserAvatar } from '../../ui/UserAvatar';
import type { TripMemberUser } from '../../../utils/tripMembers';

const COLS = 'grid-cols-[1fr_repeat(3,minmax(84px,110px))]';

interface Props {
  people: TripMemberUser[];
  /** What each person laid out. */
  byPayer: Record<string, number>;
  /** What each person's share comes to. */
  byParticipant: Record<string, number>;
  currency: string;
}

export interface PersonRow {
  id: string;
  name: string;
  avatarUrl?: string | null;
  paid: number;
  share: number;
  balance: number;
}

/**
 * One row per person, and the table adds up: every euro of the slice appears
 * once under `paid` and once under `share`, so the balances sum to zero.
 *
 * That is why an id nobody can name still gets a row. A split can outlive the
 * participant row it belonged to (removing a member does not touch their
 * expenses), and dropping it would leave a table that silently disagrees with
 * the total right above it.
 */
export function personRows(
  people: TripMemberUser[],
  byPayer: Record<string, number>,
  byParticipant: Record<string, number>,
  unknownName: string,
): PersonRow[] {
  const known = new Map(people.map(p => [p.id, p]));
  const ids = [...new Set([...known.keys(), ...Object.keys(byPayer), ...Object.keys(byParticipant)])];

  return ids
    .map(id => {
      const person = known.get(id);
      const paid   = byPayer[id] ?? 0;
      const share  = byParticipant[id] ?? 0;
      return {
        id,
        name: person ? (person.displayName || person.username) : unknownName,
        avatarUrl: person?.avatarUrl,
        paid,
        share,
        balance: paid - share,
      };
    })
    .sort((a, b) => b.share - a.share || b.paid - a.paid || a.name.localeCompare(b.name));
}

/**
 * What each traveller actually spent.
 *
 * This card used to be bars of `byPayer` alone, titled "spending by person",
 * and that is two different questions wearing one label: who laid the money out
 * is not who consumed it. Whoever puts the restaurant on their card has not
 * eaten four dinners. So the two live side by side, and the difference between
 * them — the balance — is the third column, which is the same number the
 * "who owes whom" panel below settles pair by pair.
 *
 * Every member gets a row, including whoever has paid for nothing: a table that
 * only lists payers cannot answer "did one of us spend more than the other".
 */
export function SpendingByPerson({ people, byPayer, byParticipant, currency }: Props) {
  const { t } = useTranslation();

  const rows = personRows(people, byPayer, byParticipant, t('expenses.byPerson.formerTraveller'));

  const money = (value: number) => `${currency} ${value.toFixed(2)}`;

  const balanceTone = (balance: number) => {
    if (Math.abs(balance) < 0.005) return 'text-slate-400';
    return balance > 0
      ? 'text-teal-600 dark:text-teal-400'
      : 'text-red-500 dark:text-red-400';
  };

  const headers = [
    t('expenses.byPerson.traveller'),
    t('expenses.byPerson.paid'),
    t('expenses.byPerson.share'),
    t('expenses.byPerson.balance'),
  ];

  return (
    <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-card">
      <div className="px-4 md:px-5 pt-5 pb-3">
        <p className="font-mono text-xs tracking-[0.1em] uppercase text-slate-500 dark:text-slate-400">
          {t('expenses.spendingByPerson')}
        </p>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1 leading-relaxed">
          {t('expenses.byPerson.hint')}
        </p>
      </div>

      {/* Column titles only where there are columns: below `md` each row stacks
          its own labels, the same split `LedgerRow` makes. */}
      <div className={`hidden md:grid ${COLS} gap-3 px-5 py-2.5 border-y border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30`}>
        {headers.map((header, i) => (
          <span
            key={header}
            className={`font-mono text-[11px] tracking-[0.1em] uppercase text-slate-400 ${i === 0 ? '' : 'text-right'}`}
          >
            {header}
          </span>
        ))}
      </div>

      <div className="divide-y divide-slate-50 dark:divide-slate-800/50">
        {rows.map(row => (
          <div key={row.id}>
            {/* Phone */}
            <div className="md:hidden px-4 py-3">
              <div className="flex items-center gap-2.5 mb-2">
                <UserAvatar name={row.name} avatarUrl={row.avatarUrl} size={26} />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {row.name}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: headers[1], value: money(row.paid),  tone: 'text-slate-700 dark:text-slate-300' },
                  { label: headers[2], value: money(row.share), tone: 'text-slate-700 dark:text-slate-300' },
                  { label: headers[3], value: money(row.balance), tone: balanceTone(row.balance) },
                ].map(cell => (
                  <div key={cell.label} className="min-w-0">
                    <p className="font-mono text-[10px] tracking-[0.08em] uppercase text-slate-400 truncate">
                      {cell.label}
                    </p>
                    <p className={`font-mono text-xs tabular-nums truncate ${cell.tone}`}>{cell.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Table */}
            <div className={`hidden md:grid ${COLS} gap-3 px-5 py-3 items-center`}>
              <div className="flex items-center gap-2.5 min-w-0">
                <UserAvatar name={row.name} avatarUrl={row.avatarUrl} size={28} />
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                  {row.name}
                </span>
              </div>
              <span className="font-mono text-sm tabular-nums text-right text-slate-700 dark:text-slate-300">
                {money(row.paid)}
              </span>
              <span className="font-mono text-sm tabular-nums text-right text-slate-700 dark:text-slate-300">
                {money(row.share)}
              </span>
              <span className={`font-mono text-sm tabular-nums text-right font-semibold ${balanceTone(row.balance)}`}>
                {money(row.balance)}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
